import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import store from '@/store'
import { auth } from '@/services/firebaseAdapter'
import Feed from '@/views/Feed.vue'
import Login from '@/views/Login.vue'
import Home from '@/views/Home.vue'
import Admin from '@/views/Admin.vue'
import Signup from '@/views/Signup.vue'
import PostEdit from '@/views/PostEdit.vue'
import PasswordResetRequest from '@/views/PasswordResetRequest.vue'
import EmailAction from '@/views/EmailAction.vue'
import Error404 from '@/views/404.vue'
import UserEdit from '@/views/UserEdit.vue'
import Notifications from '@/views/Notifications.vue'
import SuperAdmin from '@/views/SuperAdmin.vue'
import { OrganizationInterface } from '@/types/interfaces'

// ROUTE HELPERS

const _setOrganization = async (organizationId: string) => {
  if(!organizationId) return false
  // Load Organization
  return await store.dispatch('loadOrganization', {organizationId})
}

const _rememberOrganization = (organization: OrganizationInterface) => {
  // Remember organization in localStorage for easy access later from home
  localStorage.setItem('organizationId', organization.id)
  localStorage.setItem('organizationLogo', organization.brand.logo)
  localStorage.setItem('organizationName', organization.brand.name)
}

// ROUTES

Vue.use(VueRouter)

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      requiresAuthentication: true,
      requiresOrganization: false,
      authorizedRoles: false
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      requiresAuthentication: false,
      requiresOrganization: false,
      authorizedRoles: false
    },
    async beforeEnter(to, from, next){
      // Redirect if user is already logged
      if(store.getters.userSignedIn) next('/')
      next()
    }
  },
  {
    path: '/logout',
    name: 'Logout',
    meta: {
      requiresAuthentication: false,
      requiresOrganization: false,
      authorizedRoles: false
    },
    async beforeEnter(to, from, next){
      await store.dispatch('signOutUser')
      // Redirect to organization login if an organization was set before
      next(
        store.getters.organization.id ? `/${store.getters.organization.id}/login` : '/login'
      )
    }
  },
  {
    path: '/password-reset',
    name: 'PasswordResetRequest',
    component: PasswordResetRequest,
    meta: {
      requiresAuthentication: false,
      requiresOrganization: false,
      authorizedRoles: false
    },
  },
  {
    path: '/settings',
    name: 'UserSettings',
    component: UserEdit,
    meta: {
      requiresAuthentication: true,
      requiresOrganization: false,
      authorizedRoles: false
    }
  },
  {
    path: '/email-action',
    name: 'EmailAction',
    component: EmailAction,
    meta: {
      requiresAuthentication: false,
      requiresOrganization: false,
      authorizedRoles: false
    },
    async beforeEnter(to, from, next){
      // Redirect to Home if ...
      if(
        // ... the user is already logged in
        store.getters.userSignedIn ||
        // ... the number of URL parameters is below the required number of parameters
        Object.keys(to.query).length < auth.requiredEmailActionParameters.length || 
        // ... not all required parameters are present
        !auth.requiredEmailActionParameters.every(param => Object.keys(to.query).includes(param)) || 
        // ... the email action type value isn't supported yet
        !auth.availableEmailActionTypes.some(mode => mode == to.query.mode)
      ) return next('/')

      // Verify password reset code:
      try { await store.dispatch('resetPasswordVerifyCode', to.query.oobCode) }
      catch (error) {
        return next({
          path: '/password-reset',
          query: {
            type: "codeExpired"
          }
        })
      }

      next()
    }
  },

  {
    path: '/superadmin-347629gr2345234',
    name: 'SuperAdmin',
    component: SuperAdmin,
    meta: {
      requiresAuthentication: true,
      requiresOrganization: false,
      authorizedRoles: false
    },
  },

  // To make sure organization IDs are used as routes on the highest level, all other on the first level must come above this line!

  {
    path: '/:organizationId',
    name: 'OrganizationFeed',
    component: Feed,
    meta: {
      requiresAuthentication: true,
      requiresOrganization: true,
      authorizedRoles: ['admin', 'author', 'reader']
    }
  },
  {
    path: '/:organizationId/post/:postId/edit',
    name: 'PostEdit',
    component: PostEdit,
    meta: {
      requiresAuthentication: true,
      requiresOrganization: true,
      authorizedRoles: ['admin', 'author']
    }
  },
  {
    path: '/:organizationId/notifications',
    name: 'OrganizationNotifications',
    component: Notifications,
    meta: {
      requiresAuthentication: true,
      requiresOrganization: true,
      authorizedRoles: ['admin', 'author']
    }
  },
  {
    path: '/:organizationId/login',
    name: 'OrganizationLogin',
    component: Login,
    meta: {
      requiresAuthentication: false,
      requiresOrganization: true,
      authorizedRoles: false
    },
    async beforeEnter(to, from, next){
      // Redirect if user is already logged in
      if(store.getters.userSignedIn) next(`/${to.params.organizationId}`)
      next()
    }
  },
  {
    path: '/:organizationId/signup/:invitationKey',
    name: 'OrganizationSignup',
    component: Signup,
    meta: {
      requiresAuthentication: false,
      requiresOrganization: true,
      authorizedRoles: false
    },
    async beforeEnter(to, from, next){
      const invitation = await store.dispatch('loadInvitation', to.params.invitationKey)
      if(!invitation) return next('/')
      next()
    }
  },
  {
    path: '/:organizationId/admin',
    name: 'OrganizationAdmin',
    component: Admin,
    meta: {
      requiresAuthentication: true,
      requiresOrganization: true,
      authorizedRoles: ['admin']
    }
  },
  {
    path: '/:organizationId/settings',
    name: 'OrganizationUserSettings',
    component: UserEdit,
    meta: {
      requiresAuthentication: true,
      requiresOrganization: true,
      authorizedRoles: false
    }
  },
  // Fallback Redirect
  {
    path : '/:organizationId/*',
    name: '404',
    component: Error404,
    meta: {
      requiresAuthentication: false,
      requiresOrganization: true,
      authorizedRoles: false
    }
  },
]

const router = new VueRouter({
  mode: "history",
  routes
})

router.beforeEach(async (to, from, next) => {
  // 📌 Set organization and redirect if its not found
  const isOrganizationRequired = to.matched.some(record => record.meta.requiresOrganization)
  if(isOrganizationRequired && !await _setOrganization(to.params.organizationId)) return next({name: 'Home', query: {err: "orgNotFound"}})
  // Only if organization ID is present: remember organization for later: 
  if(store.getters.organization?.id) _rememberOrganization(store.getters.organization)
  
  // 🚦 Authentication
  const isAuthRequired = to.matched.some(record => record.meta.requiresAuthentication)
  const user = await store.dispatch('updateUserAuth')
  // Load organization user data only if a user and organization exists
  if(store.getters.organization?.id && !!user.id) {
    await store.dispatch('loadOrganizationUser', user.id)
  }
  // Redirect to login if user is not authenticated
  if(isAuthRequired && !user.signedIn){
    // redirect to organization specitif login page?
    if(isOrganizationRequired){ return next(`/${to.params.organizationId}/login`) }
    else{ return next('login') }
  }

  // 🔒 Authorization
  const isAuthorizationRequired = to.matched.some(record => record.meta.authorizedRoles)
  if(isAuthorizationRequired && store.getters.organization?.id){
    if(
      !store.getters.organizationUser.isAuthorized
      || !to.meta.authorizedRoles.includes(store.getters.organizationUser.role)
    ) return next({ name: 'Home', query: { errorType: "authorizationFailed" } })
  }
  
  next()
})

export default router
